/**
 * useShootDuration — how long the creative is on site, for the whole cart.
 *
 * Legacy sums this in its own cart context (`Cart.context.tsx`: each cart
 * product's `timeOnSiteMin/Max` plus its selected options') and renders it in
 * the cart column as "Duration onsite" (`OrderAdditionalContent`). The v2
 * Summary asks for it beside the meeting date instead, which is where it is
 * actually useful — it is the answer to "how long do I need to be there".
 *
 * ⚠️ DARK UNTIL THE BACKEND SENDS IT. `CatalogProductV2DTO` carries no
 * time-on-site field (verified against `/api-docs/v2`, 2026-08-27): the
 * `timeOnSiteMin/Max` on `CatalogProductV2` are marked `@dev-mock-only` and the
 * transform copies whatever is there, which on the live wire is nothing. So this
 * returns zeros against the real catalogue and the row hides itself. Registered
 * as DF-I30 with the BE ticket; when the field lands, this lights up with no
 * further FE work.
 *
 * Arithmetic mirrors legacy's, with two deliberate differences:
 *   - it does NOT multiply by the line's quantity, exactly as legacy did not —
 *     a second photo package does not double the time spent at the property;
 *   - it does not add per-add-on time, because `ResolvedOption` has no such
 *     field to add. Legacy's add-ons carried one. That is part of the same BE
 *     gap, so the ticket asks for both.
 */

import { useMemo } from 'react'

import type { ResolvedCatalogue } from 'models/purchaseFlowRevamp'
import { usePfCatalogue } from 'dataQueries/purchaseFlowRevamp.query'

import { usePfStore } from '../../store'
import { findVariant } from '../../store/serializeLine'

export interface ShootDuration {
  /** Minutes, 0 when unknown. */
  min: number
  /** Minutes, 0 when unknown. */
  max: number
}

/** Pure so the sum can be tested without a store or a query. */
export const sumShootDuration = (
  items: ReadonlyArray<{ variantId: string }>,
  catalogue: ResolvedCatalogue | undefined
): ShootDuration => {
  let min = 0
  let max = 0

  for (const item of items) {
    const variant = findVariant(catalogue, item.variantId)
    if (!variant) continue

    min += variant.timeOnSiteMin ?? 0
    max += variant.timeOnSiteMax ?? 0
  }

  return { min, max }
}

/**
 * Self-contained on purpose: its only consumer is the shared legacy Logistics
 * panel, and threading the catalogue in from there would have made that
 * component fetch the v2 catalogue itself — a heavy dependency for a widget the
 * old PurchaseFlow also renders. One hook to call, one hook to stub in tests,
 * same shape as `useInstructionCatalogue` / `useBillingPaymentMethods`.
 *
 * The country/locale derivation matches `useCatalogue`'s other callers, or React
 * Query serves a second cache entry (and a second GET).
 */
export const useShootDuration = (): ShootDuration => {

  const items = usePfStore((s) => s.items)

  const catalogue = usePfCatalogue().data

  return useMemo(() => sumShootDuration(items, catalogue), [catalogue, items])
}
